import React from 'react'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import { Dialog, BasicDialogBody } from '../../../legacy-design-system/modal'
import { FormattedMessage } from 'react-intl'
import { RouteToAppLink } from '../../link'
import { logError, logSuccess } from '../../toaster'
import gql from 'graphql-tag'
import { Mutation, FetchResult } from 'react-apollo'
import { PureQueryOptions } from 'apollo-client'
import { ContrastFormattedMessage } from '../../contrast-formatted-message'

interface ArchiveDialogProps extends RenderDialogProps {
  readonly organizationID: string
  readonly applicationID?: string
  readonly applicationName?: string
}

export function ConfirmArchiveAppDialog({
  isOpen,
  closeDialog,
  applicationID,
  applicationName,
  organizationID,
  archive
}: ArchiveDialogProps & {
  readonly archive: () => Promise<FetchResult<any> | void>
}) {
  return (
    <Dialog
      isOpen={isOpen}
      onCancel={closeDialog}
      renderTitle={() => (
        <FormattedMessage
          id="plural_title_archiveAppsConfirmation"
          defaultMessage="{count, plural, one {Archive Application} other {Archive Applications}}"
          values={{
            count: applicationID ? 1 : 2
          }}
        />
      )}
    >
      <BasicDialogBody
        body={
          applicationID ? (
            <FormattedMessage
              id="variable_applications_archiveAppConfirmation"
              defaultMessage="This will archive the application {applicationName}. We'll maintain the application data collected, but it will no longer appear in your list, report new findings, or be included in your portfolio score. You can restore this application at any time."
              values={{
                applicationName: (
                  <RouteToAppLink
                    organizationID={organizationID}
                    applicationID={applicationID}
                    children={applicationName}
                  />
                )
              }}
            />
          ) : (
            <FormattedMessage
              id="detail_applications_archiveMultipleApps"
              defaultMessage="This will archive the selected applications. We'll maintain the application data collected, but they will no longer appear in your list, report new findings, or be included in your portfolio score. You can restore these applications at any time."
            />
          )
        }
        confirmButtonText={<FormattedMessage id="action_archive_application" />}
        cancelButtonText={<FormattedMessage id="action_close" />}
        onCancel={closeDialog}
        onConfirm={() => {
          closeDialog()
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          archive()
        }}
      />
    </Dialog>
  )
}

const archiveApplication = gql`
  mutation ArchiveApplication($organizationID: ID!, $applicationID: ID!) {
    archiveApplication(
      input: { organizationID: $organizationID, id: $applicationID }
    ) {
      id
    }
  }
`

interface MutationProps {
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

export function ArchiveDialog({
  refetchQueries,
  applicationID,
  organizationID,
  refresh,
  ...props
}: ArchiveDialogProps & MutationProps) {
  return (
    <Mutation mutation={archiveApplication}>
      {(archiveApplication) => (
        <ContrastFormattedMessage
          id="plural_archivePrimaryApplication_appArchivedSuccessfully"
          values={{ count: 1 }}
          children={(successMessage) => (
            <ContrastFormattedMessage
              id="plural_archivePrimaryApplication_appArchivedError"
              values={{ count: 1 }}
              children={(errorMessage) => (
                <ConfirmArchiveAppDialog
                  {...props}
                  organizationID={organizationID}
                  applicationID={applicationID}
                  archive={() =>
                    archiveApplication({
                      refetchQueries,
                      variables: { applicationID, organizationID },
                      optimisticResponse: {
                        archiveApplication: {
                          id: applicationID,
                          __typename: 'Application'
                        },
                        __typename: 'Mutation'
                      }
                    })
                      .then((response) => {
                        if (response?.errors) {
                          return logError(errorMessage)
                        }

                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        refresh()
                        logSuccess(successMessage)
                      })
                      .catch((error) => {
                        logError(errorMessage)
                        return error
                      })
                  }
                />
              )}
            />
          )}
        />
      )}
    </Mutation>
  )
}

const archiveApplications = gql`
  mutation ArchiveApplications($organizationID: ID!, $applicationIDs: [ID!]!) {
    archiveApplications(
      input: { organizationID: $organizationID, ids: $applicationIDs }
    ) {
      _placeholder
    }
  }
`

interface BulkProps {
  readonly applicationIDs: string[]
}

class ArchiveMutation extends Mutation<
  any,
  {
    readonly applicationIDs: string[]
    readonly organizationID: string
  }
> {}

export function BulkArchiveDialog({
  applicationIDs,
  organizationID,
  refetchQueries,
  refresh,
  ...props
}: ArchiveDialogProps & MutationProps & BulkProps) {
  return (
    <ArchiveMutation mutation={archiveApplications}>
      {(archiveApplications) => {
        return (
          <ContrastFormattedMessage
            id="plural_archivePrimaryApplication_appArchivedSuccessfully"
            values={{ count: applicationIDs.length }}
            children={(successMessage) => (
              <ContrastFormattedMessage
                id="plural_archivePrimaryApplication_appArchivedError"
                values={{ count: applicationIDs.length }}
                children={(errorMessage) => (
                  <ConfirmArchiveAppDialog
                    {...props}
                    organizationID={organizationID}
                    archive={() => {
                      return archiveApplications({
                        refetchQueries,
                        variables: { applicationIDs, organizationID },
                        optimisticResponse: {
                          archiveApplications: {
                            _placeholder: true,
                            __typename: 'ApplicationBulkResponse'
                          },
                          __typename: 'Mutation'
                        }
                      })
                        .then((response) => {
                          if (response?.errors) {
                            return logError(errorMessage)
                          }

                          // eslint-disable-next-line @typescript-eslint/no-floating-promises
                          refresh()
                          logSuccess(successMessage)
                        })
                        .catch((error) => {
                          logError(errorMessage)
                          return error
                        })
                    }}
                  />
                )}
              />
            )}
          />
        )
      }}
    </ArchiveMutation>
  )
}

const archiveApplicationsByFilters = gql`
  mutation ArchiveApplicationsByFilters(
    $organizationID: ID!
    $excludedApplications: [ID!]!
    $filters: ApplicationFilterArg
  ) {
    archiveApplicationsByFilters(
      input: {
        organizationID: $organizationID
        excludedApplications: $excludedApplications
        filters: $filters
      }
    ) {
      _placeholder
    }
  }
`

interface BulkWithFiltersProps {
  readonly excludedApplications: string[]
  readonly filters: any
}

export function BulkArchiveWithFiltersDialog({
  excludedApplications,
  organizationID,
  filters,
  refetchQueries,
  refresh,
  ...props
}: ArchiveDialogProps & MutationProps & BulkWithFiltersProps) {
  return (
    <Mutation mutation={archiveApplicationsByFilters}>
      {(archiveApplications) => {
        return (
          <ConfirmArchiveAppDialog
            {...props}
            organizationID={organizationID}
            archive={() => {
              return archiveApplications({
                refetchQueries,
                variables: { excludedApplications, organizationID, filters },
                optimisticResponse: {
                  archiveApplicationsByFilters: {
                    _placeholder: true,
                    __typename: 'ApplicationBulkResponse'
                  },
                  __typename: 'Mutation'
                }
              }).then(refresh)
            }}
          />
        )
      }}
    </Mutation>
  )
}
